<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import clubInvitationService from '../services/clubInvitation.service'
import clubApplicationService from '../services/clubApplication.service'
import SendClubInvitationsModal from '../components/SendClubInvitationsModal.vue'
import ClubInvitationHistoryModal from '../components/ClubInvitationHistoryModal.vue'
import { CLUB_INVITATION_STATUSES, useClubInvitationStatus } from '../composables/useClubInvitationStatus'
import type {
  ClubInvitationListItem,
  ClubInvitationSendResponse,
  ClubInvitationStatus
} from '../types/clubInvitation.types'

const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()
const { statusLabel, statusBadgeClass, formatDateTime } = useClubInvitationStatus()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')

// State
const invitations = ref<ClubInvitationListItem[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const signupEnabled = ref(true)
const enablingSignup = ref(false)

// Pagination
const currentPage = ref(1)
const perPage = ref(25)
const total = ref(0)
const lastPage = ref(1)

// Filters
const searchQuery = ref('')
const statusFilter = ref<ClubInvitationStatus | ''>('')
const hasFilters = computed(() => searchQuery.value.trim() !== '' || statusFilter.value !== '')

// Selection
const selectedUuids = ref<string[]>([])
const showBulkActionsDropdown = ref(false)
const openDropdownId = ref<string | null>(null)

// Modals
const showSendModal = ref(false)
const historyUuid = ref<string | null>(null)
const confirmAction = ref<{ type: 'resend' | 'delete'; uuids: string[]; email?: string } | null>(null)
const actionInProgress = ref(false)

const statusFilterLabel = (status: ClubInvitationStatus) =>
  status === 'sent' ? t('clubInvitations.filterSent') : t(`clubInvitations.status.${status}`)

const fetchInvitations = async () => {
  if (!clubUuid.value) return
  loading.value = true
  error.value = null
  try {
    const response = await clubInvitationService.getInvitations(
      clubUuid.value,
      currentPage.value,
      perPage.value,
      searchQuery.value,
      statusFilter.value
    )
    invitations.value = response.data?.invitations || []
    total.value = response.total || 0
    lastPage.value = response.last_page || 1
    signupEnabled.value = response.signup_enabled !== false
    // Drop selections that are no longer on the list
    const visible = new Set(invitations.value.map(i => i.uuid))
    selectedUuids.value = selectedUuids.value.filter(uuid => visible.has(uuid))
  } catch (err: any) {
    console.error('Fetch invitations error:', err)
    error.value = err.response?.data?.message || t('clubInvitations.errorFetching')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  currentPage.value = 1
  fetchInvitations()
}

const handleStatusChange = () => {
  currentPage.value = 1
  selectedUuids.value = []
  fetchInvitations()
}

// Selection
const allSelected = computed(() =>
  invitations.value.length > 0 && invitations.value.every(i => selectedUuids.value.includes(i.uuid))
)

const toggleSelectAll = () => {
  selectedUuids.value = allSelected.value ? [] : invitations.value.map(i => i.uuid)
}

const toggleSelection = (uuid: string) => {
  const idx = selectedUuids.value.indexOf(uuid)
  if (idx === -1) selectedUuids.value.push(uuid)
  else selectedUuids.value.splice(idx, 1)
}

// Dropdowns
const toggleDropdown = (event: Event, uuid: string) => {
  event.stopPropagation()
  openDropdownId.value = openDropdownId.value === uuid ? null : uuid
}

const closeAllDropdowns = () => {
  openDropdownId.value = null
  showBulkActionsDropdown.value = false
}

// Signup toggle
const enableSignup = async () => {
  if (!clubUuid.value) return
  enablingSignup.value = true
  try {
    const updated = await clubApplicationService.updateSignupLink(clubUuid.value, true)
    signupEnabled.value = updated.enabled
    if (updated.message) toastStore.success(updated.message)
  } catch (err: any) {
    console.error('Enable signup error:', err)
    toastStore.error(err.response?.data?.message || t('clubInvitations.errorEnablingSignup'))
  } finally {
    enablingSignup.value = false
  }
}

// Modals
const openSendModal = () => {
  closeAllDropdowns()
  if (!signupEnabled.value) return
  showSendModal.value = true
}

const onInvitationsSent = (_response: ClubInvitationSendResponse) => {
  showSendModal.value = false
  currentPage.value = 1
  fetchInvitations()
}

const onSignupDisabled = () => {
  showSendModal.value = false
  signupEnabled.value = false
}

const openHistory = (uuid: string) => {
  closeAllDropdowns()
  historyUuid.value = uuid
}

const askResend = (uuids: string[]) => {
  closeAllDropdowns()
  // A single address goes out right away; bulk resends ask first
  if (uuids.length === 1) resend(uuids)
  else confirmAction.value = { type: 'resend', uuids }
}

const askDelete = (uuids: string[], email?: string) => {
  closeAllDropdowns()
  confirmAction.value = { type: 'delete', uuids, email }
}

const closeConfirm = () => {
  if (!actionInProgress.value) confirmAction.value = null
}

const showSendResult = (response: ClubInvitationSendResponse) => {
  if (response.processing_mode === 'sync' && (response.failed_count ?? 0) > 0) {
    toastStore.warning(response.message, 8000)
  } else {
    toastStore.success(response.message, response.processing_mode === 'async' ? 8000 : undefined)
  }
}

const resend = async (uuids: string[]) => {
  if (!signupEnabled.value) return
  actionInProgress.value = true
  try {
    const response = await clubInvitationService.resend(clubUuid.value, uuids)
    showSendResult(response)
    selectedUuids.value = selectedUuids.value.filter(uuid => !uuids.includes(uuid))
    confirmAction.value = null
    await fetchInvitations()
  } catch (err: any) {
    console.error('Resend invitations error:', err)
    const data = err.response?.data
    if (err.response?.status === 422 && data?.error === 'signup_disabled') signupEnabled.value = false
    toastStore.error(data?.message || t('clubInvitations.errorSending'))
    confirmAction.value = null
    // Every mail failed - statuses changed to "failed", show them
    if (data?.processing_mode === 'sync') await fetchInvitations()
  } finally {
    actionInProgress.value = false
  }
}

const remove = async (uuids: string[]) => {
  actionInProgress.value = true
  try {
    const response = await clubInvitationService.remove(clubUuid.value, uuids)
    toastStore.success(response.message || t('clubInvitations.deletedSuccess'))
    selectedUuids.value = selectedUuids.value.filter(uuid => !uuids.includes(uuid))
    confirmAction.value = null
    // Whole page removed — step back a page
    const removedAllVisible = invitations.value.every(i => uuids.includes(i.uuid))
    if (removedAllVisible && currentPage.value > 1) currentPage.value--
    await fetchInvitations()
  } catch (err: any) {
    console.error('Delete invitations error:', err)
    toastStore.error(err.response?.data?.message || t('clubInvitations.errorDeleting'))
  } finally {
    actionInProgress.value = false
  }
}

const runConfirmedAction = () => {
  const action = confirmAction.value
  if (!action) return
  if (action.type === 'resend') resend(action.uuids)
  else remove(action.uuids)
}

const showBulkActions = computed(() => selectedUuids.value.length > 0)

// Pagination handlers
const changePage = (page: number) => {
  currentPage.value = page
  selectedUuids.value = []
  fetchInvitations()
}

const changePerPage = () => {
  currentPage.value = 1
  selectedUuids.value = []
  fetchInvitations()
}

const fromItem = computed(() => (total.value === 0 ? 0 : (currentPage.value - 1) * perPage.value + 1))
const toItem = computed(() => Math.min(currentPage.value * perPage.value, total.value))

const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  const end = Math.min(lastPage.value, start + maxVisible - 1)
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

onMounted(() => {
  fetchInvitations()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen" @click="closeAllDropdowns">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back link -->
      <div class="mb-4">
        <router-link to="/club-applications" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('clubInvitations.backToApplications') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('clubInvitations.title') }} <span class="text-sm font-normal text-gray-600">({{ t('dashboard.found') }} {{ total }})</span>
        </h1>
        <div class="flex items-center gap-2">
          <button
            @click.stop="fetchInvitations"
            :disabled="loading"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-white text-sm md:text-sm text-xs font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300 flex items-center gap-2 md:gap-2 gap-1 disabled:opacity-50"
          >
            <svg :class="['w-4 h-4 md:w-4 md:h-4 w-3 h-3', loading ? 'animate-spin' : '']" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
            </svg>
            {{ t('clubInvitations.refresh') }}
          </button>
          <button
            @click.stop="openSendModal"
            :disabled="!signupEnabled"
            :title="!signupEnabled ? t('clubInvitations.signupDisabledBanner') : undefined"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
            </svg>
            {{ t('clubInvitations.sendToEmails') }}
          </button>
        </div>
      </div>

      <!-- Signup disabled -->
      <div
        v-if="!signupEnabled"
        class="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-amber-50 border-2 border-amber-300 rounded p-3"
      >
        <p class="text-sm text-amber-800">{{ t('clubInvitations.signupDisabledBanner') }}</p>
        <button
          @click.stop="enableSignup"
          :disabled="enablingSignup"
          class="px-3 py-1.5 bg-amber-600 text-xs font-medium text-white rounded hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap self-start sm:self-auto"
        >
          {{ enablingSignup ? t('clubInvitations.enablingSignup') : t('clubInvitations.enableSignup') }}
        </button>
      </div>

      <!-- Filters -->
      <div class="mb-4 flex flex-col sm:flex-row sm:justify-end gap-2">
        <select
          v-model="statusFilter"
          @change="handleStatusChange"
          :aria-label="t('clubInvitations.statusFilterLabel')"
          class="sm:w-56 px-2.5 py-1.5 text-sm border-2 border-gray-300 rounded bg-white focus:outline-none focus:border-blue-600"
        >
          <option value="">{{ t('clubInvitations.filterAll') }}</option>
          <option v-for="status in CLUB_INVITATION_STATUSES" :key="status" :value="status">{{ statusFilterLabel(status) }}</option>
        </select>
        <div class="flex">
          <input
            v-model="searchQuery"
            @keyup.enter="handleSearch"
            type="text"
            :placeholder="t('dashboard.search')"
            class="flex-1 sm:flex-none sm:w-48 px-2.5 py-1.5 text-sm border-2 border-r-0 border-gray-300 rounded-l focus:outline-none focus:border-blue-600"
          >
          <button
            @click="handleSearch"
            :aria-label="t('dashboard.search')"
            class="px-2.5 py-1.5 bg-blue-600 text-white rounded-r hover:bg-blue-700 border-2 border-blue-600"
          >
            <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Bulk Actions Bar -->
      <div
        v-if="showBulkActions"
        class="mb-4 flex flex-col gap-3 bg-blue-50 border-2 border-blue-300 rounded p-3"
      >
        <div class="flex items-center gap-3">
          <div class="relative">
            <button
              @click.stop="showBulkActionsDropdown = !showBulkActionsDropdown"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <span>{{ t('dashboard.optionsForSelected') }} ({{ selectedUuids.length }})</span>
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showBulkActionsDropdown"
              class="absolute left-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-lg z-50"
              @click.stop
            >
              <button
                @click="askResend([...selectedUuids])"
                :disabled="!signupEnabled"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                {{ t('clubInvitations.resend') }}
              </button>
              <button
                @click="askDelete([...selectedUuids])"
                class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                {{ t('common.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border-2 border-red-300 rounded p-3">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-sm text-gray-600">{{ t('dashboard.loading') }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-if="!loading && invitations.length > 0" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <table class="w-full text-sm">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleSelectAll"
                  :aria-label="t('clubInvitations.selectAll')"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                >
              </th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('clubInvitations.colEmail') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('clubInvitations.colLastSent') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('clubInvitations.colStatus') }}</th>
              <th class="px-3 py-2 text-left text-xs font-semibold text-gray-700">{{ t('clubInvitations.colSentBy') }}</th>
              <th class="px-3 py-2 text-right text-xs font-semibold text-gray-700">{{ t('dashboard.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invitation in invitations"
              :key="invitation.uuid"
              class="border-b border-gray-200 hover:bg-gray-50"
            >
              <td class="px-3 py-2">
                <input
                  type="checkbox"
                  :checked="selectedUuids.includes(invitation.uuid)"
                  @change="toggleSelection(invitation.uuid)"
                  :aria-label="invitation.email"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                >
              </td>
              <td class="px-3 py-2">
                <button
                  @click.stop="openHistory(invitation.uuid)"
                  class="text-blue-600 hover:text-blue-800 font-medium text-left break-all"
                >
                  {{ invitation.email }}
                </button>
              </td>
              <td class="px-3 py-2 text-gray-900 whitespace-nowrap tabular-nums">
                {{ formatDateTime(invitation.last_sent_at) }}
                <span
                  v-if="invitation.send_count > 1"
                  class="ml-1 inline-block px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-medium rounded"
                  :title="t('clubInvitations.sendCount', { count: invitation.send_count })"
                >×{{ invitation.send_count }}</span>
              </td>
              <td class="px-3 py-2">
                <span
                  :class="['inline-block px-2 py-0.5 text-xs font-medium rounded', statusBadgeClass(invitation.status)]"
                  :title="invitation.status === 'failed' && invitation.last_error ? invitation.last_error : undefined"
                >
                  {{ statusLabel(invitation) }}<template v-if="invitation.status === 'opened' && invitation.open_count > 1"> ×{{ invitation.open_count }}</template>
                </span>
                <div v-if="invitation.status_at" class="text-[11px] text-gray-500 mt-0.5 tabular-nums">{{ formatDateTime(invitation.status_at) }}</div>
              </td>
              <td class="px-3 py-2 text-gray-900">{{ invitation.sent_by?.name || '-' }}</td>
              <td class="px-2 py-2 sticky right-0 bg-inherit" style="position: relative;">
                <div class="flex items-center gap-1 justify-end">
                  <button
                    @click="toggleDropdown($event, invitation.uuid)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('dashboard.actions') }}
                  </button>
                  <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === invitation.uuid }]">
                    <button @click.stop="openHistory(invitation.uuid)">{{ t('clubInvitations.preview') }}</button>
                    <button v-if="signupEnabled" :disabled="actionInProgress" @click.stop="askResend([invitation.uuid])">{{ t('clubInvitations.resend') }}</button>
                    <button class="delete-action" @click.stop="askDelete([invitation.uuid], invitation.email)">{{ t('common.delete') }}</button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div v-if="!loading && invitations.length > 0" class="md:hidden space-y-2">
        <div
          v-for="invitation in invitations"
          :key="invitation.uuid"
          class="bg-white border-2 border-gray-300 rounded shadow-sm"
        >
          <div class="flex items-start gap-2 p-3">
            <input
              type="checkbox"
              :checked="selectedUuids.includes(invitation.uuid)"
              @change.stop="toggleSelection(invitation.uuid)"
              :aria-label="invitation.email"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded flex-shrink-0 mt-1"
            >
            <div class="flex-1 min-w-0">
              <button
                @click.stop="openHistory(invitation.uuid)"
                class="text-sm text-blue-600 hover:underline font-medium text-left break-all"
              >
                {{ invitation.email }}
              </button>
              <div class="flex flex-wrap items-center gap-1 mt-1">
                <span
                  :class="['inline-block px-2 py-0.5 text-[10px] font-medium rounded', statusBadgeClass(invitation.status)]"
                  :title="invitation.status === 'failed' && invitation.last_error ? invitation.last_error : undefined"
                >
                  {{ statusLabel(invitation) }}<template v-if="invitation.status === 'opened' && invitation.open_count > 1"> ×{{ invitation.open_count }}</template>
                </span>
                <span v-if="invitation.status_at" class="text-[11px] text-gray-500 tabular-nums">{{ formatDateTime(invitation.status_at) }}</span>
              </div>
              <div class="text-xs text-gray-600 mt-1 tabular-nums">
                {{ t('clubInvitations.colLastSent') }}: {{ formatDateTime(invitation.last_sent_at) }}<template v-if="invitation.send_count > 1"> (×{{ invitation.send_count }})</template>
              </div>
              <div v-if="invitation.sent_by" class="text-xs text-gray-600 mt-0.5">
                {{ t('clubInvitations.colSentBy') }}: {{ invitation.sent_by.name }}
              </div>
            </div>
            <div style="position: relative;">
              <button
                @click.stop="toggleDropdown($event, invitation.uuid)"
                class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
              >
                {{ t('dashboard.actions') }}
              </button>
              <div :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === invitation.uuid }]">
                <button @click.stop="openHistory(invitation.uuid)">{{ t('clubInvitations.preview') }}</button>
                <button v-if="signupEnabled" :disabled="actionInProgress" @click.stop="askResend([invitation.uuid])">{{ t('clubInvitations.resend') }}</button>
                <button class="delete-action" @click.stop="askDelete([invitation.uuid], invitation.email)">{{ t('common.delete') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && !error && invitations.length === 0" class="text-center py-12 px-4 bg-white border-2 border-gray-300 rounded">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
        </svg>
        <template v-if="hasFilters">
          <h3 class="mt-2 text-sm font-medium text-gray-900">{{ t('clubInvitations.emptyFiltered') }}</h3>
        </template>
        <template v-else>
          <h3 class="mt-2 text-sm font-medium text-gray-900">{{ t('clubInvitations.empty') }}</h3>
          <p class="mt-1 text-sm text-gray-500">{{ t('clubInvitations.emptyHint') }}</p>
          <div class="mt-6">
            <button
              @click.stop="openSendModal"
              :disabled="!signupEnabled"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ t('clubInvitations.sendToEmails') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && invitations.length > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4 bg-white border-2 border-gray-300 rounded p-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('dashboard.displaying') }} {{ fromItem }} {{ t('dashboard.to') }} {{ toItem }} {{ t('dashboard.of') }} {{ total }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('clubInvitations.perPage') }}</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <SendClubInvitationsModal
    :show="showSendModal"
    :club-uuid="clubUuid"
    :signup-enabled="signupEnabled"
    @close="showSendModal = false"
    @done="onInvitationsSent"
    @signup-disabled="onSignupDisabled"
  />

  <ClubInvitationHistoryModal
    :show="historyUuid !== null"
    :club-uuid="clubUuid"
    :invitation-uuid="historyUuid"
    @close="historyUuid = null"
  />

  <!-- Resend / delete confirmation -->
  <div
    v-if="confirmAction"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeConfirm"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
      <template v-if="confirmAction.type === 'delete'">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('clubInvitations.deleteConfirmTitle') }}</h3>
        <p class="text-sm text-gray-600 mb-4 break-words">
          {{ confirmAction.email
            ? t('clubInvitations.deleteConfirmOne', { email: confirmAction.email })
            : t('clubInvitations.deleteConfirmMany', { count: confirmAction.uuids.length }) }}
        </p>
      </template>
      <template v-else>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('clubInvitations.resendConfirmTitle') }}</h3>
        <p class="text-sm text-gray-600 mb-4">{{ t('clubInvitations.resendConfirmMany', { count: confirmAction.uuids.length }) }}</p>
      </template>
      <div class="flex justify-end gap-3">
        <button
          @click="closeConfirm"
          :disabled="actionInProgress"
          class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border border-gray-300"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          @click="runConfirmedAction"
          :disabled="actionInProgress"
          :class="[
            'px-4 py-2 text-white text-sm font-medium rounded disabled:opacity-50',
            confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
          ]"
        >
          <template v-if="confirmAction.type === 'delete'">
            {{ actionInProgress ? t('clubInvitations.deleting') : t('common.delete') }}
          </template>
          <template v-else>
            {{ actionInProgress ? t('clubInvitations.sending') : t('clubInvitations.resend') }}
          </template>
        </button>
      </div>
    </div>
  </div>
</template>
